import { memo, useEffect, useMemo, useState } from 'react';
import { Dimensions, Image } from 'react-native';
import { useNavigation } from '@bonanza-social/navigation';
import { useSelector } from '@bonanza-social/toolkit/state';
import { Text } from '@bonanza-social/moonlight';
import { selectCommunityJackpotEvents } from '../../../../state';
import { isEventEnded } from '../JackpotProgress.utils';
import {
  DEFAULT_IMAGE_DIMENSIONS,
  FALLBACK_IMAGE_DIMENSIONS,
} from './JackpotProgressHeader.content';
import { JackpotProgressHeaderProps } from './JackpotProgressHeader.types';
import CommunityJackpotImage from '../../../CommunityJackpotImage/CommunityJackpotImage';

const { width: windowWidth } = Dimensions.get('window');

function JackpotProgressHeader({ milestoneHit }: JackpotProgressHeaderProps) {
  const navigation = useNavigation();
  const [event] = useSelector(selectCommunityJackpotEvents);
  const { titleImageUrl } = event;
  const [imageDimensions, setImageDimensions] = useState(
    titleImageUrl ? DEFAULT_IMAGE_DIMENSIONS : FALLBACK_IMAGE_DIMENSIONS,
  );
  const eventEnded = useMemo(() => isEventEnded(event), [event]);

  useEffect(() => {
    if (titleImageUrl) {
      const unsubscribe = navigation.addListener('transitionEnd', () => {
        Image.getSize(titleImageUrl)?.then(({ width, height }) => {
          const aspectRatio = width / height;
          const maxWidth = windowWidth * 0.8;

          setImageDimensions({
            width: maxWidth,
            height: maxWidth / aspectRatio,
          });
        });
      });

      return unsubscribe;
    }
  }, [titleImageUrl, navigation]);

  if (eventEnded) {
    return (
      <>
        <Text
          variant="h4"
          locKey="eventEndedTitle"
          locNS="communityJackpot"
          color="darkSurfacePrimary"
          paddingBottom="space-400"
          textAlign="center"
        />
        <Text
          variant="overline"
          locKey="eventEndedDescription"
          locNS="communityJackpot"
          color="darkSurfacePrimary"
          textAlign="center"
          marginBottom="space-600"
        />
      </>
    );
  }

  if (milestoneHit) {
    return (
      <Text
        variant="h4"
        locKey="milestoneHitTitle"
        locNS="communityJackpot"
        color="darkSurfacePrimary"
        textAlign="center"
      />
    );
  }

  return (
    <CommunityJackpotImage
      url={titleImageUrl}
      fallback="communityJackpot.communityJackpotHeader"
      alignSelf="center"
      marginVertical="space-300"
      maxWidth="80%"
      {...imageDimensions}
    />
  );
}

export default memo(JackpotProgressHeader);
